import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, MapPin, Phone, Mail, Clock, Globe, Instagram, Linkedin, Youtube } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [industries, setIndustries] = useState([]);
  useEffect(() => {
    axios.get(`${API}/industries`).then(r => setIndustries(r.data.industries)).catch((e) => { console.warn("Footer industries:", e.message); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <footer data-testid="footer" className="border-t border-white/10 bg-black">
      <div className="px-6 md:px-16 lg:px-28 py-20 grid grid-cols-1 lg:grid-cols-12 gap-16">
        {/* Brand */}
        <div className="lg:col-span-4">
          <div className="flex items-center gap-3">
            <img src="https://static.wixstatic.com/media/e528db_fa64245c9e6542638a0120e98d60aedf~mv2.jpg" alt="Prospera.buzz" className="h-10 w-10 object-cover" />
            <span className="text-sm tracking-tight">Prospera.buzz</span>
          </div>
          <p className="text-zinc-500 text-xs mt-6 tracking-wider leading-relaxed max-w-sm">
            We shape how businesses are perceived. Strategic positioning, storytelling, authority building, and growth systems designed to influence perception at scale.
          </p>
          <div className="flex gap-5 mt-8">
            <a href="https://www.instagram.com/PROSPERA.BUZZ" target="_blank" rel="noopener noreferrer" data-testid="social-instagram" className="text-zinc-600 hover:text-white transition-colors"><Instagram className="w-4 h-4" /></a>
            <a href="https://www.linkedin.com/company/prospera-buzz/" target="_blank" rel="noopener noreferrer" data-testid="social-linkedin" className="text-zinc-600 hover:text-white transition-colors"><Linkedin className="w-4 h-4" /></a>
            <a href="https://www.youtube.com/channel/UCMOOD4dcjDGwpuHvk1Xf8BQ" target="_blank" rel="noopener noreferrer" data-testid="social-youtube" className="text-zinc-600 hover:text-white transition-colors"><Youtube className="w-4 h-4" /></a>
          </div>
        </div>

        <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-4 gap-10">
          {/* Capabilities */}
          <div>
            <p className="text-[10px] text-zinc-600 tracking-[0.3em] mb-5">Capabilities</p>
            <div className="space-y-3">
              <Link to="/services/brand-strategy" className="block text-xs text-zinc-500 hover:text-white transition-colors">Brand Strategy</Link>
              <Link to="/services/brand-storytelling" className="block text-xs text-zinc-500 hover:text-white transition-colors">Brand Films</Link>
              <Link to="/services/founder-branding" className="block text-xs text-zinc-500 hover:text-white transition-colors">Founder Branding</Link>
              <Link to="/services/ai-search-optimization" className="block text-xs text-zinc-500 hover:text-white transition-colors">AI Visibility</Link>
              <Link to="/services/geo" className="block text-xs text-zinc-500 hover:text-white transition-colors">GEO</Link>
              <Link to="/services/seo" className="block text-xs text-zinc-500 hover:text-white transition-colors">SEO</Link>
              <Link to="/services/google-ads" className="block text-xs text-zinc-500 hover:text-white transition-colors">Google Ads</Link>
              <Link to="/services/meta-ads" className="block text-xs text-zinc-500 hover:text-white transition-colors">Meta Ads</Link>
              <Link to="/ad-studio" className="block text-xs text-zinc-500 hover:text-white transition-colors">Ad Studio</Link>
            </div>
          </div>

          {/* Industries */}
          <div>
            <p className="text-[10px] text-zinc-600 tracking-[0.3em] mb-5">Industries</p>
            <div className="space-y-3">
              <Link to="/industry/food-hospitality" className="block text-xs text-zinc-500 hover:text-white transition-colors">Hospitality</Link>
              <Link to="/industry/real-estate" className="block text-xs text-zinc-500 hover:text-white transition-colors">Real Estate</Link>
              <Link to="/fashion" className="block text-xs text-zinc-500 hover:text-white transition-colors">Fashion & Lifestyle</Link>
              <Link to="/industry/healthcare-wellness" className="block text-xs text-zinc-500 hover:text-white transition-colors">Healthcare</Link>
              <Link to="/industry/saas-technology" className="block text-xs text-zinc-500 hover:text-white transition-colors">Technology</Link>
              <Link to="/industry/ecommerce-retail" className="block text-xs text-zinc-500 hover:text-white transition-colors">E-Commerce</Link>
              <Link to="/industry/startups-smes" className="block text-xs text-zinc-500 hover:text-white transition-colors">Startups</Link>
            </div>
          </div>

          {/* Company */}
          <div>
            <p className="text-[10px] text-zinc-600 tracking-[0.3em] mb-5">Company</p>
            <div className="space-y-3">
              <Link to="/academy" className="block text-xs text-zinc-500 hover:text-white transition-colors">Thinking</Link>
              <Link to="/careers" className="block text-xs text-zinc-500 hover:text-white transition-colors">Careers</Link>
              <Link to="/booking" className="block text-xs text-zinc-500 hover:text-white transition-colors">Contact</Link>
              <Link to="/agency-partners" className="block text-xs text-zinc-500 hover:text-white transition-colors">For Agencies</Link>
              <Link to="/for-investors" className="block text-xs text-zinc-500 hover:text-white transition-colors">For Investors</Link>
              <Link to="/for-artists" className="block text-xs text-zinc-500 hover:text-white transition-colors">For Artists</Link>
              <Link to="/creators" className="block text-xs text-zinc-500 hover:text-white transition-colors">For Creators</Link>
              <Link to="/talent" className="block text-xs text-zinc-500 hover:text-white transition-colors">Talent</Link>
            </div>
          </div>

          {/* Contact */}
          <div>
            <p className="text-[10px] text-zinc-600 tracking-[0.3em] mb-5">Contact</p>
            <div className="space-y-4">
              <a href="tel:+916239547599" className="flex items-start gap-2 text-xs text-zinc-400 hover:text-white transition-colors">
                <Phone className="w-3 h-3 mt-0.5 flex-shrink-0" /> +91 6239 547 599
              </a>
              <a href="https://wa.me/919041344420?text=Hi%20Prospera" target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 text-xs text-zinc-400 hover:text-white transition-colors">
                <Phone className="w-3 h-3 mt-0.5 flex-shrink-0" /> Whatsapp
              </a>
              <a href="mailto:prospera.uae@gmail.com" className="flex items-start gap-2 text-xs text-zinc-400 hover:text-white transition-colors">
                <Mail className="w-3 h-3 mt-0.5 flex-shrink-0" /> prospera.uae@gmail.com
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Offices */}
      <div className="border-t border-white/5 px-6 md:px-16 lg:px-28 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
          {[
            { city: "Chandigarh", address: "India", tz: "GMT+5:30" },
            { city: "Dubai", address: "UAE", tz: "GMT+4" },
            { city: "Toronto", address: "Canada", tz: "GMT-5" }
          ].map(o => (
            <div key={o.city} className="flex gap-3">
              <MapPin className="w-3 h-3 text-zinc-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-zinc-300 tracking-wider">{o.city}</p>
                <p className="text-[10px] text-zinc-600 mt-1 tracking-wider">{o.address}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom */}
      <div className="border-t border-white/5 px-6 md:px-16 lg:px-28 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-zinc-700 text-[10px] tracking-[0.2em]">{new Date().getFullYear()} Prospera.buzz. All rights reserved.</p>
        <div className="flex gap-6">
          <span className="text-[10px] text-zinc-700 tracking-wider">Privacy policy</span>
          <span className="text-[10px] text-zinc-700 tracking-wider">Terms of service</span>
        </div>
      </div>
    </footer>
  );
}
